/**
 * TaskPhaseForm / AddTaskPhaseColumn / TaskPhaseEditButton
 *
 * Componentes de criação e edição rápida de fases direto no Kanban de Tarefas.
 * Espelha o padrão do AddPhaseColumn (Kanban de Negócios), adaptado aos
 * campos de uma fase de tarefa (nome, cor e "fase final").
 *
 * Toda a visibilidade é controlada por permissão (props canCreate/canEdit);
 * a validação real continua sendo feita server-side pela RPC upsert_task_phase.
 *
 * @version 1.0.0
 * RELEASE: 2026-08-06 v1.0.0 - Criação inicial
 */

import { useState } from 'react';
import { Plus, Check, X, Loader2, Flag, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

// Paleta de 20 cores (mesma usada no Kanban de Negócios/Conversas)
export const TASK_PHASE_COLORS = [
  // Linha 1 - Cores primárias vibrantes
  '#3b82f6', '#22c55e', '#ef4444', '#f97316', '#eab308',
  // Linha 2 - Tons alternativos
  '#8b5cf6', '#ec4899', '#06b6d4', '#14b8a6', '#84cc16',
  // Linha 3 - Tons pastel/suaves
  '#60a5fa', '#4ade80', '#f87171', '#fb923c', '#facc15',
  // Linha 4 - Tons neutros e escuros
  '#a855f7', '#f472b6', '#6b7280', '#374151', '#1f2937',
];

/** Dados submetidos pelo formulário de fase */
export interface TaskPhaseFormValues {
  name: string;
  color: string;
  isFinal: boolean;
}

interface TaskPhaseFormProps {
  initialValues?: Partial<TaskPhaseFormValues>;
  submitLabel: string;
  onSubmit: (values: TaskPhaseFormValues) => Promise<void>;
  onCancel: () => void;
}

/**
 * Formulário compartilhado entre criação e edição de fase.
 */
function TaskPhaseForm({ initialValues, submitLabel, onSubmit, onCancel }: TaskPhaseFormProps) {
  const [name, setName] = useState(initialValues?.name ?? '');
  const [color, setColor] = useState(initialValues?.color || TASK_PHASE_COLORS[0]);
  const [isFinal, setIsFinal] = useState(initialValues?.isFinal ?? false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Envia o formulário (nome é obrigatório)
  const handleSubmit = async () => {
    if (!name.trim() || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await onSubmit({ name: name.trim(), color, isFinal });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      {/* Nome da fase */}
      <Input
        placeholder="Nome da fase..."
        value={name}
        maxLength={100}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') handleSubmit();
          if (e.key === 'Escape') onCancel();
        }}
        autoFocus
        className="mb-3"
      />

      {/* Seletor de cores - grid 5x4 */}
      <div className="grid grid-cols-5 gap-1.5 mb-3">
        {TASK_PHASE_COLORS.map((c) => (
          <button
            key={c}
            type="button"
            className={cn(
              'w-6 h-6 rounded-full transition-all',
              color === c && 'ring-2 ring-offset-2 ring-primary'
            )}
            style={{ backgroundColor: c }}
            onClick={() => setColor(c)}
          />
        ))}
      </div>

      {/* Switch de fase final */}
      <div className="flex items-center justify-between gap-2 p-2 mb-3 rounded-md border border-border/60 bg-muted/30">
        <div className="flex items-center gap-1.5">
          <Flag className={cn('h-4 w-4', isFinal ? 'text-emerald-500' : 'text-muted-foreground/40')} />
          <Label className="text-xs">Fase final</Label>
        </div>
        <Switch checked={isFinal} onCheckedChange={setIsFinal} />
      </div>

      {/* Ações */}
      <div className="flex gap-2">
        <Button size="sm" onClick={handleSubmit} disabled={!name.trim() || isSubmitting} className="flex-1">
          {isSubmitting ? (
            <Loader2 className="h-4 w-4 animate-spin mr-1" />
          ) : (
            <Check className="h-4 w-4 mr-1" />
          )}
          {submitLabel}
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel} disabled={isSubmitting}>
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

interface AddTaskPhaseColumnProps {
  onAdd: (values: TaskPhaseFormValues) => Promise<void>;
  isLoading?: boolean;
  /** Controle de visibilidade por permissão (tasks_phases.can_create) */
  canCreate?: boolean;
}

/**
 * Coluna tracejada "Nova Fase" exibida ao final do board.
 */
export function AddTaskPhaseColumn({ onAdd, isLoading = false, canCreate = true }: AddTaskPhaseColumnProps) {
  const [isOpen, setIsOpen] = useState(false);

  // Sem permissão → não renderiza nada (nem placeholder)
  if (!canCreate) return null;

  return (
    <div className="w-72 shrink-0">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              'w-full h-auto py-3 border-2 border-dashed',
              'hover:border-primary/50 hover:bg-primary/5',
              'transition-colors'
            )}
            disabled={isLoading}
          >
            <Plus className="h-4 w-4 mr-2" />
            Nova Fase
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[280px] p-3" align="start" side="bottom" sideOffset={4}>
          {/* Chave força remount para limpar o formulário a cada abertura */}
          <TaskPhaseForm
            key={isOpen ? 'open' : 'closed'}
            submitLabel="Criar"
            onSubmit={async (values) => {
              await onAdd(values);
              setIsOpen(false);
            }}
            onCancel={() => setIsOpen(false)}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

interface TaskPhaseEditButtonProps {
  phaseName: string;
  phaseColor: string;
  phaseIsFinal: boolean;
  onSave: (values: TaskPhaseFormValues) => Promise<void>;
}

/**
 * Ícone de lápis no header da coluna: edição rápida da fase.
 * Só deve ser renderizado quando o usuário tem permissão de editar fases.
 */
export function TaskPhaseEditButton({
  phaseName,
  phaseColor,
  phaseIsFinal,
  onSave,
}: TaskPhaseEditButtonProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 text-muted-foreground hover:text-foreground"
                onClick={(e) => e.stopPropagation()}
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="top">Editar fase</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <PopoverContent className="w-[280px] p-3" align="start" side="bottom" sideOffset={4}>
        <TaskPhaseForm
          key={isOpen ? 'open' : 'closed'}
          submitLabel="Salvar"
          initialValues={{ name: phaseName, color: phaseColor, isFinal: phaseIsFinal }}
          onSubmit={async (values) => {
            await onSave(values);
            setIsOpen(false);
          }}
          onCancel={() => setIsOpen(false)}
        />
      </PopoverContent>
    </Popover>
  );
}
